import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Checkbox, DesktopDatePicker, MenuItem, TextField } from 'components';

import { dayjs, useSelector, useTranslation } from 'third-party';

import {
  CreditGrantType,
  OrgCredit,
  OrgCreditFeatures,
  useSuperAdmin,
} from 'modules/superAdmin';

import { superAdminSelectors } from 'selectors';

type Props = {
  organizationId: string;
};

// `as const` keeps the literal types that t() checks its keys against.
const saveLabelKey = {
  [CreditGrantType.None]: 'superAdmin.usage.plan.savePlanOnly',
  [CreditGrantType.Monthly]: 'superAdmin.usage.plan.savePlan',
  [CreditGrantType.Lifetime]: 'superAdmin.usage.plan.addCredits',
  [CreditGrantType.Unlimited]: 'superAdmin.usage.plan.saveUnlimited',
} as const satisfies Record<CreditGrantType, string>;

const planTypeKey = {
  [CreditGrantType.None]: 'superAdmin.usage.plan.typeMonthly',
  [CreditGrantType.Monthly]: 'superAdmin.usage.plan.typeMonthly',
  [CreditGrantType.Lifetime]: 'superAdmin.usage.plan.typeLifetime',
  [CreditGrantType.Unlimited]: 'superAdmin.usage.plan.typeUnlimited',
} as const satisfies Record<CreditGrantType, string>;

const usageHeaderKey = {
  [CreditGrantType.None]: 'superAdmin.usage.monthHeader',
  [CreditGrantType.Monthly]: 'superAdmin.usage.monthHeader',
  [CreditGrantType.Lifetime]: 'superAdmin.usage.lifetimeHeader',
  [CreditGrantType.Unlimited]: 'superAdmin.usage.unlimitedHeader',
} as const satisfies Record<CreditGrantType, string>;

export const OrganizationUsageTab: React.FC<Props> = ({ organizationId }) => {
  const { t } = useTranslation();
  const {
    clearSuperAdminOrganization,
    disableOrg,
    enableOrg,
    loadOrgCredit,
    loadOrgPlans,
    loadOrgUsage,
    setOrgCredit,
  } = useSuperAdmin();

  const [valueInput, setValueInput] = useState<string>('30');
  const [grantInput, setGrantInput] = useState<CreditGrantType>(
    CreditGrantType.None,
  );
  const [planStartDate, setPlanStartDate] = useState<string>('');
  const [planInput, setPlanInput] = useState<string>('');
  const [features, setFeatures] = useState<OrgCreditFeatures>({
    maxBankAccounts: 1,
    showBadge: false,
    hasCheckDesigner: false,
    hasCustomEmailTemplates: false,
    hasApiAccess: false,
    hasPrioritySupport: false,
  });
  const [planEndDate, setPlanEndDate] = useState<string>('');

  const usage = useSelector(superAdminSelectors.orgUsage);
  const usagePending = useSelector(superAdminSelectors.loadOrgUsagePending);
  const orgCredit = useSelector(superAdminSelectors.orgCredit);
  const saveGrantPending = useSelector(superAdminSelectors.setOrgCreditPending);
  const disablePending = useSelector(superAdminSelectors.disableOrgPending);
  const orgPlans = useSelector(superAdminSelectors.orgPlans);
  const selectedPlan = orgPlans.find(it => it.code === planInput) ?? null;

  // Picking a plan seeds the grant with that plan's allowance, so a super admin adjusts from the
  // plan's own number instead of typing one from scratch.
  const onPlanChange = (code: string) => {
    setPlanInput(code);
    const plan = orgPlans.find(it => it.code === code);
    if (!plan) return;

    setValueInput(String(plan.includedChecks));
    setFeatures({
      maxBankAccounts: plan.maxBankAccounts,
      showBadge: plan.showBadge,
      hasCheckDesigner: plan.hasCheckDesigner,
      hasCustomEmailTemplates: plan.hasCustomEmailTemplates,
      hasApiAccess: plan.hasApiAccess,
      hasPrioritySupport: plan.hasPrioritySupport,
    });

    // A plan bills monthly, so default the grant window to the month ahead.
    if (!planStartDate && !planEndDate) {
      setPlanStartDate(dayjs().format('YYYY-MM-DD'));
      setPlanEndDate(dayjs().add(1, 'month').format('YYYY-MM-DD'));
    }
  };

  const creditMode = orgCredit?.grantType ?? CreditGrantType.None;
  const creditIsLifetime = creditMode === CreditGrantType.Lifetime;

  useEffect(() => {
    if (organizationId) {
      loadOrgUsage(organizationId);
      loadOrgCredit(organizationId);
    }

    return () => {
      clearSuperAdminOrganization();
    };
  }, [
    organizationId,
    loadOrgUsage,
    loadOrgCredit,
    clearSuperAdminOrganization,
  ]);

  useEffect(() => {
    if (organizationId) {
      loadOrgPlans(organizationId);
    }
  }, [organizationId, loadOrgPlans]);

  useEffect(() => {
    if (orgCredit?.planCode) {
      setPlanInput(orgCredit.planCode);
    }
  }, [orgCredit]);

  useEffect(() => {
    if (orgCredit) {
      setGrantInput(orgCredit.grantType);
      setFeatures({
        maxBankAccounts: orgCredit.maxBankAccounts,
        showBadge: orgCredit.showBadge,
        hasCheckDesigner: orgCredit.hasCheckDesigner,
        hasCustomEmailTemplates: orgCredit.hasCustomEmailTemplates,
        hasApiAccess: orgCredit.hasApiAccess,
        hasPrioritySupport: orgCredit.hasPrioritySupport,
      });
      setPlanStartDate(
        orgCredit.startDate ? orgCredit.startDate.slice(0, 10) : '',
      );
      setPlanEndDate(orgCredit.endDate ? orgCredit.endDate.slice(0, 10) : '');
      setValueInput(String(orgCredit.value));
    }
  }, [orgCredit, creditIsLifetime, creditMode]);

  const usageMode = usage?.grantType ?? CreditGrantType.None;
  const inputMode = grantInput;
  const usageLimit = usage?.limit ?? 0;
  const usageTotalUsed = usage?.totalUsed ?? 0;
  const usageRemaining =
    usage?.remaining ?? Math.max(0, usageLimit - usageTotalUsed);
  const usagePct =
    usageLimit > 0 ? Math.min(100, (usageTotalUsed / usageLimit) * 100) : 0;
  // A plan or a monthly grant both meter per month, so both can be over.
  const usageIsOver =
    (usageMode === CreditGrantType.None ||
      usageMode === CreditGrantType.Monthly) &&
    usagePct >= 100;

  const isSaved =
    !!orgCredit &&
    grantInput === orgCredit.grantType &&
    planInput === (orgCredit.planCode ?? '') &&
    parseInt(valueInput, 10) === orgCredit.value &&
    planStartDate === (orgCredit.startDate?.slice(0, 10) ?? '') &&
    planEndDate === (orgCredit.endDate?.slice(0, 10) ?? '') &&
    features.maxBankAccounts === orgCredit.maxBankAccounts &&
    features.showBadge === orgCredit.showBadge &&
    features.hasCheckDesigner === orgCredit.hasCheckDesigner &&
    features.hasCustomEmailTemplates === orgCredit.hasCustomEmailTemplates &&
    features.hasApiAccess === orgCredit.hasApiAccess &&
    features.hasPrioritySupport === orgCredit.hasPrioritySupport;

  // Unlimited carries neither an amount nor a window, so it has nothing left to validate.
  const datesRequired = inputMode === CreditGrantType.Monthly;
  const canSaveCredit =
    inputMode === CreditGrantType.None ||
    inputMode === CreditGrantType.Unlimited ||
    (!!parseInt(valueInput, 10) &&
      parseInt(valueInput, 10) > 0 &&
      (!datesRequired || (!!planStartDate && !!planEndDate)));

  const onSaveCredit = useCallback(async () => {
    if (!organizationId) return;
    const parsed = parseInt(valueInput, 10);
    const needsValue =
      inputMode === CreditGrantType.Monthly ||
      inputMode === CreditGrantType.Lifetime;

    if (needsValue) {
      if (isNaN(parsed) || parsed < 1) return;
      if (datesRequired && (!planStartDate || !planEndDate)) return;
    }

    // Unlimited and lifetime carry no window; everything else records the period it applies to, so
    // the dates are sent whenever they are set and come back prepopulated on the next load.
    const hasWindow =
      inputMode !== CreditGrantType.Unlimited &&
      inputMode !== CreditGrantType.Lifetime;

    await setOrgCredit(
      organizationId,
      needsValue ? parsed : 0,
      inputMode,
      hasWindow && planStartDate ? new Date(planStartDate).toISOString() : null,
      hasWindow && planEndDate ? new Date(planEndDate).toISOString() : null,
      planInput || null,
      features,
    );
    loadOrgUsage(organizationId);
    loadOrgCredit(organizationId);
  }, [
    organizationId,
    valueInput,
    inputMode,
    datesRequired,
    planStartDate,
    planEndDate,
    planInput,
    features,
    setOrgCredit,
    loadOrgUsage,
    loadOrgCredit,
  ]);

  const statusChip = (credit: OrgCredit) => {
    if (credit.isExpired) {
      return (
        <Chip
          label={t('superAdmin.usage.plan.expired')}
          size="small"
          color="error"
        />
      );
    }
    if (credit.isActive) {
      return (
        <Chip
          label={t('superAdmin.usage.plan.active')}
          size="small"
          color="success"
        />
      );
    }
    return <Chip label={t('superAdmin.usage.plan.inactive')} size="small" />;
  };

  const progressColor = () => {
    if (usageIsOver) return 'error';
    if (usagePct >= 80) return 'warning';
    return 'primary';
  };

  // Which of the three usage panels to render once the figures have loaded.
  const showPanel = (mode: CreditGrantType) =>
    !usagePending && usageMode === mode;

  // The stored figure reads differently per mode: a pool, a per-month cap, or nothing at all.
  const allowanceLabel = (value: number) => {
    if (creditMode === CreditGrantType.Unlimited) {
      return t('superAdmin.usage.plan.unlimitedValue');
    }
    if (creditMode === CreditGrantType.Lifetime) {
      return t('superAdmin.usage.plan.creditsValue', { count: value });
    }
    return t('superAdmin.usage.plan.monthlyValue', { count: value });
  };

  const onDisableOrg = useCallback(async () => {
    if (!organizationId) return;
    await disableOrg(organizationId);
    loadOrgUsage(organizationId);
  }, [organizationId, disableOrg, loadOrgUsage]);

  const onEnableOrg = useCallback(async () => {
    if (!organizationId) return;
    await enableOrg(organizationId);
    loadOrgUsage(organizationId);
  }, [organizationId, enableOrg, loadOrgUsage]);

  return (
    <Box px={3.5} py={2.5} display="flex" flexDirection="column" gap={3.5}>
      <Box
        sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxWidth: '52rem',
        }}
      >
        <Typography fontWeight={600} fontSize="0.88rem" mb={0.5}>
          {t('superAdmin.usage.plan.header')}
        </Typography>
        <Typography fontSize="0.78rem" color="text.secondary" mb={2}>
          {t('superAdmin.usage.plan.combinedHint')}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" mb={1}>
          <TextField
            select
            size="small"
            label={t('superAdmin.usage.plan.planField')}
            value={planInput}
            onChange={e => onPlanChange(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {orgPlans
              .filter(it => it.isActive)
              .map(it => (
                <MenuItem key={it.code} value={it.code}>
                  {it.name}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            select
            size="small"
            label={t('superAdmin.usage.plan.grantField')}
            value={grantInput}
            onChange={e => setGrantInput(Number(e.target.value))}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value={CreditGrantType.None}>
              {t('superAdmin.usage.plan.grantNone')}
            </MenuItem>
            <MenuItem value={CreditGrantType.Monthly}>
              {t('superAdmin.usage.plan.grantMonthly')}
            </MenuItem>
            <MenuItem value={CreditGrantType.Lifetime}>
              {t('superAdmin.usage.plan.grantLifetime')}
            </MenuItem>
            <MenuItem value={CreditGrantType.Unlimited}>
              {t('superAdmin.usage.plan.grantUnlimited')}
            </MenuItem>
          </TextField>
        </Box>

        {selectedPlan && (
          <Box mb={2}>
            <Typography fontSize="0.78rem" color="text.secondary" mb={0.75}>
              {t('superAdmin.usage.plan.planIncludes')}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <TextField
                size="small"
                type="number"
                label={t('superAdmin.usage.plan.maxBanks')}
                value={features.maxBankAccounts}
                onChange={e =>
                  setFeatures({
                    ...features,
                    maxBankAccounts: Number(e.target.value),
                  })
                }
                helperText={t('superAdmin.usage.plan.maxBanksHelper')}
                sx={{ width: '9rem' }}
              />
              {(
                [
                  ['hasCheckDesigner', 'billing.plan.feature.checkDesigner'],
                  [
                    'hasCustomEmailTemplates',
                    'billing.plan.feature.emailTemplates',
                  ],
                  ['hasApiAccess', 'billing.plan.feature.apiAccess'],
                  [
                    'hasPrioritySupport',
                    'billing.plan.feature.prioritySupport',
                  ],
                  ['showBadge', 'billing.plan.feature.badge'],
                ] as const
              ).map(([field, labelKey]) => (
                <FormControlLabel
                  key={field}
                  control={
                    <Checkbox
                      size="small"
                      checked={features[field]}
                      onChange={e =>
                        setFeatures({ ...features, [field]: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Typography fontSize="0.82rem">{t(labelKey)}</Typography>
                  }
                />
              ))}
            </Box>
          </Box>
        )}

        <Box
          display="flex"
          flexWrap="wrap"
          gap={2}
          mb={2}
          alignItems="flex-start"
        >
          {(inputMode === CreditGrantType.Monthly ||
            inputMode === CreditGrantType.Lifetime) && (
            <TextField
              size="small"
              type="number"
              label={t(
                inputMode === CreditGrantType.Lifetime
                  ? 'superAdmin.usage.plan.creditsToAdd'
                  : 'superAdmin.usage.plan.monthlyLimit',
              )}
              value={valueInput}
              onChange={e => setValueInput(e.target.value)}
              inputProps={{ min: 1, style: { width: '7rem' } }}
            />
          )}

          {inputMode !== CreditGrantType.Unlimited &&
            inputMode !== CreditGrantType.Lifetime && (
              <>
                <DesktopDatePicker
                  label={t('superAdmin.usage.plan.from')}
                  format="MM/DD/YYYY"
                  value={planStartDate ? dayjs(planStartDate) : null}
                  onChange={val =>
                    setPlanStartDate(val ? val.format('YYYY-MM-DD') : '')
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 160 },
                      required: true,
                    } as any,
                    field: {
                      clearable: true,
                      onClear: () => setPlanStartDate(''),
                    },
                  }}
                />

                <DesktopDatePicker
                  label={t('superAdmin.usage.plan.till')}
                  format="MM/DD/YYYY"
                  value={planEndDate ? dayjs(planEndDate) : null}
                  onChange={val =>
                    setPlanEndDate(val ? val.format('YYYY-MM-DD') : '')
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 160 },
                      required: true,
                    } as any,
                    field: {
                      clearable: true,
                      onClear: () => setPlanEndDate(''),
                    },
                  }}
                />
              </>
            )}

          <Button
            variant="contained"
            size="small"
            disabled={saveGrantPending || !canSaveCredit || isSaved}
            onClick={onSaveCredit}
            sx={{
              whiteSpace: 'nowrap',
              alignSelf: 'center',
              color: 'primary.contrastText',
            }}
          >
            {t(
              isSaved ? 'superAdmin.usage.plan.saved' : saveLabelKey[inputMode],
            )}
          </Button>
        </Box>

        {/* Current record info table */}
        {orgCredit?.isManualGrant && (
          <Box sx={{ overflowX: 'auto' }}>
            <Typography fontSize="0.78rem" color="text.secondary" mb={0.5}>
              {t('superAdmin.usage.plan.current')}
            </Typography>
            <MuiTable size="small" sx={{ maxWidth: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t('superAdmin.column.type')}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t(
                      creditIsLifetime
                        ? 'superAdmin.usage.plan.allowance'
                        : 'superAdmin.usage.plan.limit',
                    )}
                  </TableCell>
                  {creditIsLifetime && (
                    <>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {t('superAdmin.usage.plan.used')}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {t('superAdmin.usage.plan.remaining')}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t('superAdmin.usage.plan.from')}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t('superAdmin.usage.plan.till')}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                    {t('superAdmin.usage.plan.status')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {t(planTypeKey[creditMode])}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {allowanceLabel(orgCredit.value)}
                  </TableCell>
                  {creditIsLifetime && (
                    <>
                      {/* Same figures as the panel below, dashed until usage loads so a
                          pending fetch does not read as a real 0. */}
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {usage
                          ? t('superAdmin.usage.plan.creditsValue', {
                              count: usageTotalUsed,
                            })
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {usage
                          ? t('superAdmin.usage.plan.creditsValue', {
                              count: usageRemaining,
                            })
                          : '—'}
                      </TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {orgCredit.startDate
                      ? orgCredit.startDate.slice(0, 10)
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>
                    {orgCredit.endDate ? orgCredit.endDate.slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>{statusChip(orgCredit)}</TableCell>
                </TableRow>
              </TableBody>
            </MuiTable>
          </Box>
        )}
      </Box>

      <Box>
        <Typography fontWeight={600} fontSize="0.88rem" mb={2}>
          {t(usageHeaderKey[usageMode])}
        </Typography>

        {usagePending && (
          <Box display="flex" justifyContent="center" pt={4}>
            <CircularProgress />
          </Box>
        )}

        {showPanel(CreditGrantType.Unlimited) && (
          <Box
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              maxWidth: '52rem',
            }}
          >
            <Typography
              sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1, mb: 0.5 }}
            >
              {t('superAdmin.usage.unlimitedValue')}
            </Typography>
            <Typography fontSize="0.78rem" color="text.secondary">
              {t('superAdmin.usage.unlimitedHint')}
            </Typography>
            <Typography fontSize="0.78rem" color="text.secondary" mt={0.5}>
              {t('superAdmin.usage.unlimitedUsed', { count: usageTotalUsed })}
            </Typography>
          </Box>
        )}

        {showPanel(CreditGrantType.Lifetime) && (
          <Box
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: usageRemaining <= 0 ? 'error.main' : 'divider',
              borderRadius: 1,
              maxWidth: '52rem',
              backgroundColor:
                usageRemaining <= 0 ? 'rgba(211,47,47,0.07)' : 'transparent',
            }}
          >
            <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
              <Typography
                sx={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: usageRemaining <= 0 ? 'error.main' : 'text.primary',
                }}
              >
                {usageRemaining}
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                {t('superAdmin.usage.creditsUnit')}
              </Typography>
            </Box>
            <Typography fontSize="0.78rem" color="text.secondary">
              {t(
                usageRemaining <= 0
                  ? 'superAdmin.usage.noCreditsRemaining'
                  : 'superAdmin.usage.creditsRemainingHint',
              )}
            </Typography>
            <Typography fontSize="0.78rem" color="text.secondary" mt={0.5}>
              {t('superAdmin.usage.lifetimeBreakdown', {
                allowance: usageLimit,
                used: usageTotalUsed,
              })}
            </Typography>
            {creditIsLifetime && (
              <Typography fontSize="0.72rem" color="text.secondary" mt={0.5}>
                {t('superAdmin.usage.lifetimeCountedSince', {
                  date: dayjs(orgCredit?.createdOn).format('MM/DD/YYYY'),
                })}
              </Typography>
            )}
          </Box>
        )}

        {showPanel(CreditGrantType.Monthly) && (
          <Box
            sx={{
              p: 2.5,
              border: '1px solid',
              borderColor: usageIsOver ? 'error.main' : 'divider',
              borderRadius: 1,
              maxWidth: '52rem',
              backgroundColor: usageIsOver
                ? 'rgba(211,47,47,0.07)'
                : 'transparent',
            }}
          >
            <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
              <Typography
                sx={{
                  fontSize: '2rem',
                  fontWeight: 700,
                  lineHeight: 1,
                  color: usageIsOver ? 'error.main' : 'text.primary',
                }}
              >
                {usageTotalUsed}
              </Typography>
              <Typography sx={{ fontSize: '1.1rem', color: 'text.secondary' }}>
                / {usageLimit}
              </Typography>
            </Box>
            <Typography fontSize="0.78rem" color="text.secondary" mb={2}>
              {t('superAdmin.usage.checksRemaining', {
                count: usageRemaining,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usagePct}
              color={progressColor()}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 2.5,
          border: '1px solid',
          borderColor: usage?.isDisabled ? 'error.main' : 'divider',
          borderRadius: 1,
          maxWidth: '52rem',
          backgroundColor: usage?.isDisabled
            ? 'rgba(211,47,47,0.05)'
            : 'transparent',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography fontWeight={600} fontSize="0.88rem" mb={0.5}>
              {t('superAdmin.usage.access.header')}
            </Typography>
            <Typography fontSize="0.78rem" color="text.secondary">
              {t(
                usage?.isDisabled
                  ? 'superAdmin.usage.access.disabledHint'
                  : 'superAdmin.usage.access.activeHint',
              )}
            </Typography>
          </Box>
          {usage?.isDisabled ? (
            <Button
              variant="outlined"
              size="small"
              color="success"
              disabled={disablePending}
              onClick={onEnableOrg}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('superAdmin.usage.access.enable')}
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={disablePending}
              onClick={onDisableOrg}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {t('superAdmin.usage.access.disable')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};
